import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Check, Film, Star, Trash2, Upload, Play, Loader2, Share2, Send, Download, X, Flame } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { SocialShareDialog } from "@/components/content/SocialShareDialog";
import { Loader } from "@/components/common/Loader";
import { EmptyState } from "@/components/common/EmptyState";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, fileUrl } from "@/lib/api";
import { uploadChunked } from "@/lib/upload";
import { cn, formatLocalDateTime } from "@/lib/utils";

export default function Clips() {
    const [clips, setClips] = useState(null);
    const [filter, setFilter] = useState("all");
    const [sort, setSort] = useState("recent");
    const [active, setActive] = useState(null);
    const [publishing, setPublishing] = useState(null);
    const [playError, setPlayError] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [hoverStar, setHoverStar] = useState(0);
    const [selecting, setSelecting] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);
    const [bulkDeleting, setBulkDeleting] = useState(false);
    const fileRef = useRef(null);
    const playbackRetryRef = useRef(new Set());

    const load = async () => {
        const { data } = await api.get("/clips");
        setClips(data.clips);
        return data.clips;
    };
    useEffect(() => { api.get("/clips").then(({ data }) => setClips(data.clips)); }, []);

    const recoverPlayback = async () => {
        if (!active?.id || playbackRetryRef.current.has(active.id)) {
            setPlayError(active?.id || null);
            return;
        }
        playbackRetryRef.current.add(active.id);
        try {
            const latest = await load();
            const updated = latest.find((clip) => clip.id === active.id);
            if (!updated?.video_media_url) throw new Error("Media is not ready");
            setActive(updated);
            setPlayError(null);
        } catch {
            setPlayError(active.id);
        }
    };

    const selectedCount = selectedIds.length;
    const isSelected = (id) => selectedIds.includes(id);
    const toggleSelected = (id) => {
        setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
    };
    const clearSelection = () => {
        setSelecting(false);
        setSelectedIds([]);
    };

    const rate = async (value) => {
        if (!active) return;
        try {
            await api.post(`/clips/${active.id}/feedback`, { rating: value });
            toast.success("Thanks — your AI coach will learn from this.");
            setClips((prev) => prev?.map((c) => (c.id === active.id ? { ...c, user_rating: value } : c)));
            setActive((a) => (a ? { ...a, user_rating: value } : a));
        } catch { toast.error("Could not save your rating."); }
    };

    const removeFromModal = async () => {
        if (!active || !window.confirm("Delete this clip? This cannot be undone.")) return;
        try {
            await api.delete(`/clips/${active.id}`);
            toast("Clip deleted.");
            setActive(null);
            load();
        } catch { toast.error("Could not delete clip."); }
    };

    const toggleStar = async (id, e) => {
        e.stopPropagation();
        await api.patch(`/clips/${id}/star`);
        load();
    };
    const remove = async (id, e) => {
        e.stopPropagation();
        await api.delete(`/clips/${id}`);
        toast("Clip deleted.");
        load();
    };
    const removeSelected = async () => {
        if (!selectedIds.length || bulkDeleting) return;
        const count = selectedIds.length;
        if (!window.confirm(`Delete ${count} selected clip${count === 1 ? "" : "s"}? This cannot be undone.`)) return;
        setBulkDeleting(true);
        try {
            const results = await Promise.allSettled(selectedIds.map((id) => api.delete(`/clips/${id}`)));
            const deleted = results.filter((r) => r.status === "fulfilled").length;
            const failed = count - deleted;
            if (deleted) toast.success(`${deleted} clip${deleted === 1 ? "" : "s"} deleted.`);
            if (failed) toast.error(`${failed} clip${failed === 1 ? "" : "s"} could not be deleted.`);
            clearSelection();
            load();
        } finally {
            setBulkDeleting(false);
        }
    };
    const share = async (id, e) => {
        e?.stopPropagation();
        try {
            const { data } = await api.post(`/clips/${id}/share`);
            const url = `${window.location.origin}${data.path}`;
            try { await navigator.clipboard.writeText(url); toast.success("Public share link copied to clipboard!"); }
            catch { toast.success(`Share link: ${url}`); }
        } catch { toast.error("Could not create share link."); }
    };
    const upload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const ext = file.name.includes(".") ? file.name.split(".").pop().toLowerCase() : "mp4";
            await uploadChunked(file, { kind: "clip", ext, params: { title: file.name, event_type: "Hotkey Trigger", game: "warzone" } });
            toast.success("Clip uploaded.");
            load();
        } catch { toast.error("Upload failed."); }
        finally { setUploading(false); e.target.value = ""; }
    };

    if (!clips) return <Loader label="Loading clips" />;
    const playableClips = clips.filter((c) => Boolean(c.video_media_url || c.video_url || c.storage_path));
    const events = ["all", "starred", ...Array.from(new Set(playableClips.map((c) => c.event_type)))];
    const shown = playableClips
        .filter((c) => filter === "all" || (filter === "starred" ? c.starred : c.event_type === filter))
        .slice()
        .sort((a, b) => sort === "rating"
            ? (b.clip_score || 0) - (a.clip_score || 0)
            : new Date(b.created_at || 0) - new Date(a.created_at || 0));

    return (
        <div className="ww-fade-up">
            <PageHeader title="Clips" subtitle="Auto-generated highlights from your sessions.">
                <button data-testid="select-clips-btn" onClick={() => setSelecting(true)}
                    className="h-10 px-4 rounded-sm border border-border text-white hover:bg-white/5 font-semibold flex items-center gap-2">
                    <Check className="w-4 h-4" /> Select
                </button>
                <button data-testid="upload-clip-btn" onClick={() => fileRef.current?.click()} disabled={uploading}
                    className="h-10 px-5 rounded-sm bg-volt text-black font-semibold hover:bg-[#33ceff] flex items-center gap-2 disabled:opacity-60">
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload Clip
                </button>
                <input ref={fileRef} type="file" accept="video/*" className="hidden" onChange={upload} data-testid="upload-clip-input" />
            </PageHeader>

            <div className="flex flex-wrap items-center gap-2 mb-6">
                <button data-testid="select-clips-filter-btn" onClick={() => setSelecting(true)}
                    className={cn("px-3 py-1.5 rounded-sm text-xs font-mono uppercase tracking-wider border transition-colors",
                        selecting ? "border-volt text-volt bg-volt/10" : "border-border text-white hover:bg-white/5")}>
                    Select Clips
                </button>
                {events.map((ev) => (
                    <button key={ev} onClick={() => setFilter(ev)} data-testid={`filter-${ev}`}
                        className={cn("px-3 py-1.5 rounded-sm text-xs font-mono uppercase tracking-wider border transition-colors",
                            filter === ev ? "border-volt text-volt bg-volt/10" : "border-border text-muted-foreground hover:text-white")}>
                        {ev}
                    </button>
                ))}
                <div className="ml-auto flex items-center gap-1" data-testid="clip-sort">
                    <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mr-1">Sort</span>
                    {[["recent", "Recent"], ["rating", "Top rated"]].map(([key, label]) => (
                        <button key={key} onClick={() => setSort(key)} data-testid={`sort-${key}`}
                            className={cn("px-3 py-1.5 rounded-sm text-xs font-mono uppercase tracking-wider border transition-colors flex items-center gap-1",
                                sort === key ? "border-volt text-volt bg-volt/10" : "border-border text-muted-foreground hover:text-white")}>
                            {key === "rating" && <Flame className="w-3 h-3" />}{label}
                        </button>
                    ))}
                </div>
            </div>

            {selecting && (
                <div className="sticky top-16 z-20 mb-4 min-h-12 border border-volt/35 bg-obsidian/95 backdrop-blur px-3 py-2 flex flex-wrap items-center justify-between gap-2" data-testid="clip-select-toolbar">
                    <div className="text-sm text-white font-semibold">{selectedCount} selected</div>
                    <div className="flex flex-wrap items-center gap-2">
                        <button onClick={() => setSelectedIds(shown.map((c) => c.id))} data-testid="select-all-clips"
                            className="h-8 px-3 rounded-sm border border-border text-xs font-semibold text-white hover:bg-white/5">
                            Select all
                        </button>
                        <button onClick={clearSelection} data-testid="cancel-select-clips"
                            className="h-8 px-3 rounded-sm border border-border text-xs font-semibold text-muted-foreground hover:text-white hover:bg-white/5 flex items-center gap-1.5">
                            <X className="w-3.5 h-3.5" /> Cancel
                        </button>
                        <button onClick={removeSelected} disabled={!selectedCount || bulkDeleting} data-testid="delete-selected-clips"
                            className="h-8 px-3 rounded-sm border border-alert/50 text-xs font-semibold text-alert hover:bg-alert/10 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5">
                            {bulkDeleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} Delete selected
                        </button>
                    </div>
                </div>
            )}

            {shown.length === 0 ? (
                <EmptyState icon={Film} title="No clips yet" subtitle="Start a live capture or upload a video to build your highlight library." />
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {shown.map((c) => (
                        <div key={c.id} data-testid={`clip-card-${c.id}`} onClick={() => {
                            if (selecting) { toggleSelected(c.id); return; }
                            setPlayError(null); setActive(c);
                        }}
                            className={cn("group bg-surface border rounded-sm overflow-hidden cursor-pointer transition-colors",
                                isSelected(c.id) ? "border-volt ring-1 ring-volt/60" : "border-border hover:border-white/25")}>
                            <div className="relative aspect-video bg-obsidian">
                                {c.thumbnail_path || c.thumbnail_url
                                    ? <img loading="lazy" decoding="async" fetchPriority="low" src={c.thumbnail_path ? fileUrl(c.thumbnail_media_url) : c.thumbnail_url} alt={c.title} className="w-full h-full object-cover opacity-85 group-hover:opacity-100 transition-opacity" />
                                    : <div className="w-full h-full grid place-items-center ww-grid-bg"><Film className="w-8 h-8 text-muted-foreground" /></div>}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                                <div className="absolute inset-0 grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity">
                                    <span className="grid place-items-center w-11 h-11 rounded-full bg-volt/90 text-black"><Play className="w-5 h-5 ml-0.5" /></span>
                                </div>
                                <span className="absolute top-2 left-2 text-[10px] font-mono uppercase tracking-wider text-volt bg-black/60 px-2 py-0.5 rounded-sm">{c.event_type}</span>
                                {typeof c.clip_score === "number" && c.clip_score > 0 && (
                                    <span data-testid={`clip-score-${c.id}`}
                                        className={cn("absolute bottom-2 left-2 inline-flex items-center gap-1 text-[10px] font-mono font-bold bg-black/70 px-1.5 py-0.5 rounded-sm",
                                            c.clip_score >= 90 ? "text-alert" : c.clip_score >= 80 ? "text-warn" : "text-muted-foreground")}
                                        title={`AI highlight rating ${c.clip_score}/100`}>
                                        <Flame className="w-3 h-3" />{c.clip_score}
                                    </span>
                                )}
                                {selecting
                                    ? <span className={cn("absolute top-2 right-2 grid place-items-center w-7 h-7 rounded-sm border",
                                        isSelected(c.id) ? "bg-volt border-volt text-black" : "bg-black/60 border-white/40 text-transparent")}
                                    data-testid={`select-${c.id}`}>
                                        <Check className="w-4 h-4" />
                                    </span>
                                    : <button onClick={(e) => toggleStar(c.id, e)} data-testid={`star-${c.id}`}
                                        className="absolute top-2 right-2 grid place-items-center w-7 h-7 rounded-sm bg-black/60 hover:bg-black/80">
                                        <Star className={cn("w-3.5 h-3.5", c.starred ? "fill-warn text-warn" : "text-white")} />
                                    </button>}
                            </div>
                            <div className="p-3 flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                    <div className="text-sm text-white truncate">{c.title}</div>
                                    <div className="text-[11px] text-muted-foreground font-mono">{c.duration_sec}s · {c.game}</div>
                                    {c.created_at && <div className="text-[10px] text-muted-foreground/70 font-mono mt-0.5" data-testid={`clip-date-${c.id}`}>{formatLocalDateTime(c.created_at)}</div>}
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    <button onClick={(e) => share(c.id, e)} disabled={selecting} data-testid={`share-${c.id}`} className="text-muted-foreground hover:text-volt disabled:opacity-40 disabled:hover:text-muted-foreground" title="Share clip">
                                        <Share2 className="w-4 h-4" />
                                    </button>
                                    <button onClick={(e) => remove(c.id, e)} disabled={selecting} data-testid={`delete-${c.id}`} className="text-muted-foreground hover:text-alert disabled:opacity-40 disabled:hover:text-muted-foreground" title="Delete">
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
                <DialogContent className="bg-surface border-border max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-heading">{active?.title}</DialogTitle>
                        <DialogDescription className="font-mono text-xs">{active?.event_type} · {active?.game}{active?.created_at ? ` · ${formatLocalDateTime(active.created_at)}` : ""}</DialogDescription>
                    </DialogHeader>
                    <div className="aspect-video bg-obsidian border border-border rounded-sm overflow-hidden">
                        {active?.video_url && playError !== active.id
                            ? <video key={`${active.id}:${active.video_media_url}`} src={fileUrl(active.video_media_url)} controls autoPlay playsInline preload="metadata"
                                className="w-full h-full" data-testid="clip-player"
                                onError={recoverPlayback} />
                            : active?.video_url && playError === active.id
                                ? <div className="relative w-full h-full grid place-items-center text-center px-4" data-testid="clip-play-error">
                                    <div>
                                        <p className="text-sm text-white font-semibold mb-1">Can’t play this clip here</p>
                                        <p className="text-xs text-muted-foreground mb-3">Your browser may not support this video codec.</p>
                                        <a href={fileUrl(active.video_media_url)} download className="inline-flex items-center gap-1.5 h-9 px-3 rounded-sm bg-volt text-black font-bold text-sm" data-testid="clip-download-btn">
                                            <Download className="w-4 h-4" /> Download clip
                                        </a>
                                    </div>
                                </div>
                            : (active?.thumbnail_path || active?.thumbnail_url)
                                ? <div className="relative w-full h-full">
                                    <img src={active?.thumbnail_path ? fileUrl(active.thumbnail_media_url) : active?.thumbnail_url} alt={active?.title} className="w-full h-full object-cover" />
                                    <div className="absolute inset-x-0 bottom-0 bg-black/75 px-3 py-2 text-[11px] font-mono flex items-center gap-1.5" data-testid="clip-image-only">
                                        {["pending", "processing"].includes(active?.video_status)
                                            ? <><Loader2 className="w-3.5 h-3.5 shrink-0 animate-spin text-volt" /> <span className="text-volt">Processing — your recorded reel is uploading or finishing in the background. It'll play here shortly.</span></>
                                            : <><Film className="w-3.5 h-3.5 shrink-0 text-warn" /> <span className="text-warn">Image-only highlight — no video recorded. Upload or screen-capture a clip to play it back.</span></>}
                                    </div>
                                </div>
                                : <div className="w-full h-full grid place-items-center"><p className="text-xs text-muted-foreground font-mono">No media available.</p></div>}
                    </div>

                    {active && (
                        <div className="rounded-sm border border-border bg-obsidian/40 p-3 flex items-center justify-between gap-3 flex-wrap" data-testid="clip-review">
                            <div>
                                <div className="text-sm text-white font-semibold">Rate this highlight</div>
                                <div className="text-[11px] text-muted-foreground">Teaches your AI coach what to clip next time.</div>
                            </div>
                            <div className="flex items-center gap-1" onMouseLeave={() => setHoverStar(0)}>
                                {[1, 2, 3, 4, 5].map((n) => (
                                    <button key={n} data-testid={`clip-rate-${n}`} onClick={() => rate(n)} onMouseEnter={() => setHoverStar(n)} className="p-0.5" aria-label={`Rate ${n} stars`}>
                                        <Star className={cn("w-6 h-6 transition-colors", (hoverStar || active?.user_rating || 0) >= n ? "fill-warn text-warn" : "text-muted-foreground hover:text-white")} />
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {active && (
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                            <button onClick={(e) => share(active.id, e)} data-testid="dialog-share-clip"
                                className="h-10 rounded-sm border border-volt/50 text-volt hover:bg-volt/10 font-semibold flex items-center justify-center gap-2 transition-colors">
                                <Share2 className="w-4 h-4" /> Share Link
                            </button>
                            <button onClick={() => setPublishing(active)} disabled={!active.video_media_url} data-testid="dialog-publish-clip"
                                className="h-10 rounded-sm border border-volt/50 text-volt hover:bg-volt/10 font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                                <Send className="w-4 h-4" /> Publish / Share
                            </button>
                            <button onClick={removeFromModal} data-testid="dialog-delete-clip"
                                className="h-10 rounded-sm border border-alert/40 text-alert hover:bg-alert/10 font-semibold flex items-center justify-center gap-2 transition-colors">
                                <Trash2 className="w-4 h-4" /> Delete
                            </button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
            <SocialShareDialog asset={publishing} open={!!publishing} onOpenChange={(open) => !open && setPublishing(null)} />
        </div>
    );
}
